import { useCallback, useEffect, useRef, useState } from 'react';
import { IconDeviceMobile, IconEraser, IconUpload, IconX } from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { createSignatureDesktopRelay } from '../../../lib/utils/signaturePeerRelay';
import SignatureMobileScanQrPanel from '../../../pages/user/tools/image/signature-pad/SignatureMobileScanQrPanel';
import { generateRelaySlug } from '../../../pages/user/tools/image/signature-pad/signaturePadRelayUtils';
import { COLOR_SWATCHES } from '../constants';
import { ColorGrid, LabeledRow, Segmented } from '../ui/prims';

const CANVAS_W = 620;
const CANVAS_H = 220;

const SIGN_FONTS = [
    { id: 'galada', label: 'Galada', css: 'Galada, cursive' },
    { id: 'alkatra', label: 'Alkatra', css: 'Alkatra, cursive' },
    { id: 'atma', label: 'Atma', css: 'Atma, cursive' },
    { id: 'cursive', label: 'Cursive', css: '"Brush Script MT", "Segoe Script", cursive' },
];

/**
 * Signature builder — draw, type or upload. Always returns a transparent PNG
 * data URL plus its pixel size so the caller can place it at the right ratio.
 */
export default function SignModal({ onClose, onApply }) {
    const { t } = useTranslation();
    const [mode, setMode] = useState('draw');
    const [color, setColor] = useState('#1C1815');
    const [typed, setTyped] = useState('');
    const [font, setFont] = useState('galada');
    const [uploaded, setUploaded] = useState(null);
    const [hasInk, setHasInk] = useState(false);
    // Phone hand-off: the signature travels browser-to-browser over the PeerJS
    // broker the signature-pad tool already uses — nothing touches our servers.
    const [relaySlug, setRelaySlug] = useState('');
    const [phoneImage, setPhoneImage] = useState(null);

    const canvasRef = useRef(null);
    const drawingRef = useRef(false);
    const fileRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || mode !== 'draw') return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = CANVAS_W * dpr;
        canvas.height = CANVAS_H * dpr;
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
        setHasInk(false);
    }, [mode]);

    const point = (e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: ((e.clientX - rect.left) / rect.width) * CANVAS_W,
            y: ((e.clientY - rect.top) / rect.height) * CANVAS_H,
        };
    };

    const start = (e) => {
        drawingRef.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        const ctx = canvasRef.current.getContext('2d');
        const p = point(e);
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = color;
        ctx.lineWidth = 3;
    };
    const move = (e) => {
        if (!drawingRef.current) return;
        const ctx = canvasRef.current.getContext('2d');
        const p = point(e);
        ctx.lineTo(p.x, p.y);
        ctx.stroke();
        setHasInk(true);
    };
    const end = () => { drawingRef.current = false; };

    const clear = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasInk(false);
    };

    /** Crop a canvas to its non-transparent pixels so the stamp has no padding. */
    const trimmedDataUrl = useCallback((canvas) => {
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
        let minX = canvas.width; let minY = canvas.height; let maxX = -1; let maxY = -1;
        for (let y = 0; y < canvas.height; y += 1) {
            for (let x = 0; x < canvas.width; x += 1) {
                if (data[(y * canvas.width + x) * 4 + 3] > 8) {
                    if (x < minX) minX = x;
                    if (y < minY) minY = y;
                    if (x > maxX) maxX = x;
                    if (y > maxY) maxY = y;
                }
            }
        }
        if (maxX < 0) return null;
        const pad = 6;
        const x = Math.max(0, minX - pad);
        const y = Math.max(0, minY - pad);
        const w = Math.min(canvas.width - x, maxX - minX + pad * 2);
        const h = Math.min(canvas.height - y, maxY - minY + pad * 2);
        const out = document.createElement('canvas');
        out.width = w;
        out.height = h;
        out.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, w, h);
        return { src: out.toDataURL('image/png'), width: w, height: h };
    }, []);

    const buildTyped = useCallback(() => {
        const scale = 3;
        const fontSize = 64;
        const css = SIGN_FONTS.find((f) => f.id === font).css;
        const measure = document.createElement('canvas').getContext('2d');
        measure.font = `${fontSize * scale}px ${css}`;
        const width = Math.ceil(measure.measureText(typed).width) + 40 * scale;
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(40, width);
        canvas.height = Math.round(fontSize * 1.9 * scale);
        const ctx = canvas.getContext('2d');
        ctx.font = `${fontSize * scale}px ${css}`;
        ctx.fillStyle = color;
        ctx.textBaseline = 'middle';
        ctx.fillText(typed, 20 * scale, canvas.height / 2);
        return trimmedDataUrl(canvas);
    }, [typed, font, color, trimmedDataUrl]);

    const readImageFile = useCallback(async (file) => {
        const src = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(file);
        });
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = reject;
            el.src = src;
        });
        return { src, width: img.naturalWidth, height: img.naturalHeight };
    }, []);

    const handleUpload = async (file) => {
        if (!file) return;
        setUploaded(await readImageFile(file));
    };

    /* ------------------------------------------------------- phone hand-off */

    useEffect(() => {
        if (mode !== 'phone') return undefined;
        if (!relaySlug) { setRelaySlug(generateRelaySlug()); return undefined; }

        const stop = createSignatureDesktopRelay(relaySlug, async (files) => {
            if (!files?.length) return;
            try {
                setPhoneImage(await readImageFile(files[0]));
            } catch { /* ignore a corrupt transfer; the user can rescan */ }
        });
        return () => stop();
    }, [mode, relaySlug, readImageFile]);

    const apply = () => {
        if (mode === 'draw') {
            const result = trimmedDataUrl(canvasRef.current);
            if (result) onApply(result);
        } else if (mode === 'type') {
            const result = typed.trim() ? buildTyped() : null;
            if (result) onApply(result);
        } else if (mode === 'phone') {
            if (phoneImage) onApply(phoneImage);
        } else if (uploaded) {
            onApply(uploaded);
        }
        onClose();
    };

    const canApply = (mode === 'draw' && hasInk)
        || (mode === 'type' && typed.trim().length > 0)
        || (mode === 'upload' && !!uploaded)
        || (mode === 'phone' && !!phoneImage);

    return (
        <div className="fixed inset-0 z-[9100] flex items-center justify-center bg-[#1C1815]/45 p-4">
            <div className="w-full max-w-[680px] overflow-hidden rounded-2xl border border-[#ECE8E4] bg-white shadow-[0_24px_64px_rgba(28,24,21,0.28)] dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex h-14 items-center justify-between border-b border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <h2 className="text-[15px] font-semibold text-[#1C1815] dark:text-zinc-100">
                        {t('pdfEditor.sign.title')}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={t('common.close')}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-[#6B635C] hover:bg-[#F4F1ED] dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                        <IconX size={18} stroke={1.8} />
                    </button>
                </div>

                <div className="p-4">
                    <div className="mb-3">
                        <Segmented
                            value={mode}
                            onChange={setMode}
                            options={[
                                { value: 'draw', label: t('pdfEditor.sign.draw') },
                                { value: 'type', label: t('pdfEditor.sign.type') },
                                { value: 'upload', label: t('pdfEditor.sign.upload') },
                                { value: 'phone', label: t('pdfEditor.sign.phone'), icon: IconDeviceMobile },
                            ]}
                        />
                    </div>

                    {mode === 'draw' ? (
                        <div className="relative">
                            <canvas
                                ref={canvasRef}
                                onPointerDown={start}
                                onPointerMove={move}
                                onPointerUp={end}
                                onPointerCancel={end}
                                style={{ width: '100%', aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}
                                className="touch-none rounded-xl border border-dashed border-[#D8D2CC] bg-[repeating-linear-gradient(45deg,#fbfaf8_0_10px,#f4f1ed_10px_20px)] dark:border-zinc-700"
                            />
                            <button
                                type="button"
                                onClick={clear}
                                className="absolute right-2 top-2 flex h-8 items-center gap-1.5 rounded-lg border border-[#ECE8E4] bg-white px-2.5 text-[12px] font-medium text-[#3D3833] dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
                            >
                                <IconEraser size={14} stroke={1.8} />
                                {t('pdfEditor.sign.clear')}
                            </button>
                        </div>
                    ) : null}

                    {mode === 'type' ? (
                        <div>
                            <input
                                value={typed}
                                onChange={(e) => setTyped(e.target.value)}
                                placeholder={t('pdfEditor.sign.typePlaceholder')}
                                className="mb-3 h-11 w-full rounded-xl border border-[#ECE8E4] bg-white px-3 text-[14px] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
                            />
                            <div className="grid grid-cols-2 gap-2">
                                {SIGN_FONTS.map((f) => (
                                    <button
                                        key={f.id}
                                        type="button"
                                        onClick={() => setFont(f.id)}
                                        className={`flex h-16 items-center justify-center overflow-hidden rounded-xl border px-3 ${
                                            font === f.id ? 'border-primary ring-2 ring-primary/25' : 'border-[#ECE8E4] dark:border-zinc-700'
                                        }`}
                                        style={{ fontFamily: f.css, fontSize: 26, color }}
                                    >
                                        <span className="truncate">{typed || f.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    {mode === 'upload' ? (
                        <div>
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="hidden"
                                onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }}
                            />
                            <button
                                type="button"
                                onClick={() => fileRef.current?.click()}
                                className="flex h-40 w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#D8D2CC] bg-[#FBFAF8] text-[13px] font-medium text-[#6B635C] hover:border-primary dark:border-zinc-700 dark:bg-zinc-800/40 dark:text-zinc-400"
                            >
                                {uploaded ? (
                                    <img src={uploaded.src} alt="" className="max-h-28 object-contain" />
                                ) : (
                                    <>
                                        <IconUpload size={22} stroke={1.7} />
                                        {t('pdfEditor.sign.uploadHint')}
                                    </>
                                )}
                            </button>
                        </div>
                    ) : null}

                    {mode === 'phone' ? (
                        <div>
                            {phoneImage ? (
                                <div className="flex h-56 flex-col items-center justify-center gap-3 rounded-xl border border-[#ECE8E4] bg-[#FBFAF8] dark:border-zinc-700 dark:bg-zinc-800/40">
                                    <img src={phoneImage.src} alt="" className="max-h-32 object-contain" />
                                    <p className="text-[12.5px] font-medium text-[#1E9E5E]">
                                        {t('pdfEditor.sign.phoneReceived')}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => { setPhoneImage(null); setRelaySlug(generateRelaySlug()); }}
                                        className="rounded-lg border border-[#ECE8E4] px-3 py-1.5 text-[12px] font-medium text-[#3D3833] dark:border-zinc-700 dark:text-zinc-200"
                                    >
                                        {t('pdfEditor.sign.phoneAgain')}
                                    </button>
                                </div>
                            ) : (
                                <SignatureMobileScanQrPanel
                                    sessionSlug={relaySlug}
                                    inModal
                                    sessionReady={!!relaySlug}
                                    onNewSession={() => setRelaySlug(generateRelaySlug())}
                                />
                            )}
                        </div>
                    ) : null}

                    {mode !== 'upload' && mode !== 'phone' ? (
                        <div className="mt-3">
                            <LabeledRow label={t('pdfEditor.options.color')}>
                                <ColorGrid colors={COLOR_SWATCHES.slice(0, 8)} value={color} onChange={setColor} />
                            </LabeledRow>
                        </div>
                    ) : null}
                </div>

                <div className="flex h-14 items-center justify-end gap-2 border-t border-[#ECE8E4] px-4 dark:border-zinc-800">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex h-9 items-center rounded-lg border border-[#ECE8E4] px-4 text-[13px] font-medium text-[#3D3833] dark:border-zinc-700 dark:text-zinc-200"
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="button"
                        onClick={apply}
                        disabled={!canApply}
                        className="flex h-9 items-center rounded-lg bg-primary px-4 text-[13px] font-semibold text-white hover:bg-primary-700 disabled:opacity-45"
                    >
                        {t('pdfEditor.sign.apply')}
                    </button>
                </div>
            </div>
        </div>
    );
}
